import { initializeApp, type FirebaseOptions } from "@firebase/app";
import { browserLocalPersistence, indexedDBLocalPersistence, initializeAuth, type Auth } from "@firebase/auth";

/**
 * Firebase Hosting serves the project's web configuration at a reserved URL,
 * so no configuration is baked into the build. In development Vite proxies it.
 */
async function loadOptions(): Promise<FirebaseOptions> {
  const response = await fetch("/__/firebase/init.json");
  if (!response.ok) {
    throw new Error(`Firebase configuration unavailable: HTTP ${response.status}`);
  }
  const options = (await response.json()) as FirebaseOptions;
  // Hosting serves the sign-in handler on every domain of the site. Using the
  // current one keeps sign-in first-party, which browsers that block
  // third-party storage require.
  const local = location.hostname === "localhost" || location.hostname === "127.0.0.1";
  return local ? options : { ...options, authDomain: location.host };
}

let auth: Auth | undefined;

export async function initFirebase(): Promise<Auth> {
  const app = initializeApp(await loadOptions());
  // No popup resolver here: it would load Google's sign-in frame on every
  // page. The Google button passes it when it is actually used.
  auth = initializeAuth(app, { persistence: [indexedDBLocalPersistence, browserLocalPersistence] });
  auth.languageCode = "he";
  return auth;
}

export function firebaseAuth(): Auth {
  if (!auth) {
    throw new Error("initFirebase has not run");
  }
  return auth;
}
